import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  ProjectReportEmployee,
  ProjectReportEmployeeOptions,
  ProjectReportPeriod,
  ProjectTaskReport,
  ProjectTaskReportEmployeeSummary,
  ProjectTaskReportQuery,
  ProjectTaskReportRow,
  ProjectTaskReportSubtask,
  ProjectTaskReportSubtaskAssignee,
  ProjectTaskReportTimeEntry,
} from '../types/projectReport';
import { calendarDateFromApi } from '../utils/dateTime';
import {
  DEFAULT_PROJECT_REPORT_PERIOD,
  PROJECT_REPORT_PERIOD_OPTIONS,
  projectTaskReportParams,
} from '../utils/projectReport';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function positiveId(value: unknown): number | null {
  const parsed = numberOrNull(value);
  return parsed !== null && parsed > 0 ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function objects(value: unknown): ObjectValue[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is ObjectValue =>
          Boolean(item) && typeof item === 'object' && !Array.isArray(item),
      )
    : [];
}

function normalizePeriod(value: unknown): ProjectReportPeriod {
  const text = textOrNull(value)?.toUpperCase();
  return (
    PROJECT_REPORT_PERIOD_OPTIONS.find(option => option.value === text)?.value ??
    DEFAULT_PROJECT_REPORT_PERIOD
  );
}

function normalizeEmployee(row: ObjectValue): ProjectReportEmployee | null {
  const employeeId = positiveId(row.employeeId);
  if (!employeeId) return null;
  const employeeNumber = textOrNull(row.employeeNumber);
  return {
    employeeId,
    employeeNumber,
    displayName: textOrNull(row.displayName) ?? employeeNumber ?? 'Employee',
  };
}

function normalizeSubtaskAssignee(
  row: ObjectValue,
): ProjectTaskReportSubtaskAssignee | null {
  const employeeId = positiveId(row.employeeId);
  if (!employeeId) return null;
  return {
    employeeId,
    employeeName: textOrNull(row.employeeName) ?? 'Employee',
    memberRole: textOrNull(row.memberRole),
  };
}

function normalizeSubtask(row: ObjectValue): ProjectTaskReportSubtask | null {
  const subtaskId = positiveId(row.subtaskId);
  if (!subtaskId) return null;
  return {
    subtaskId,
    // Kept as-is (no trim or cut) so long descriptions show in full.
    subtaskName: typeof row.subtaskName === 'string' ? row.subtaskName : '',
    isCompleted: row.isCompleted === true,
    assignees: objects(row.assignees)
      .map(normalizeSubtaskAssignee)
      .filter((item): item is ProjectTaskReportSubtaskAssignee => item != null),
  };
}

function clockOrNull(value: unknown): string | null {
  const text = textOrNull(value);
  return text && /^\d{2}:\d{2}$/.test(text) ? text : null;
}

function normalizeTimeEntry(row: ObjectValue): ProjectTaskReportTimeEntry | null {
  const timeLogId = positiveId(row.timeLogId);
  if (!timeLogId) return null;
  return {
    timeLogId,
    date: calendarDateFromApi(row.date),
    startTime: clockOrNull(row.startTime),
    endTime: clockOrNull(row.endTime),
    durationMinutes: numberOrNull(row.durationMinutes) ?? 0,
  };
}

function normalizeRow(row: ObjectValue): ProjectTaskReportRow | null {
  const taskId = positiveId(row.taskId);
  const employeeId = positiveId(row.employeeId);
  const projectId = positiveId(row.projectId);
  if (!taskId || !employeeId || !projectId) return null;
  return {
    projectId,
    projectName: textOrNull(row.projectName) ?? 'Project',
    taskId,
    taskName: textOrNull(row.taskName) ?? 'Task',
    employeeId,
    employeeNumber: textOrNull(row.employeeNumber),
    employeeName: textOrNull(row.employeeName) ?? 'Employee',
    taskStatus: textOrNull(row.taskStatus),
    isCompleted: row.isCompleted === true,
    taskDate: calendarDateFromApi(row.taskDate),
    dueDate: calendarDateFromApi(row.dueDate),
    completedDate: calendarDateFromApi(row.completedDate),
    timeEstimateMinutes: numberOrNull(row.timeEstimateMinutes),
    spentMinutesInRange: numberOrNull(row.spentMinutesInRange) ?? 0,
    timeSpentMinutes: numberOrNull(row.timeSpentMinutes) ?? 0,
    firstWorkedOn: calendarDateFromApi(row.firstWorkedOn),
    lastWorkedOn: calendarDateFromApi(row.lastWorkedOn),
    timeLogCount: numberOrNull(row.timeLogCount) ?? 0,
    subtaskTotal: numberOrNull(row.subtaskTotal) ?? 0,
    subtaskCompleted: numberOrNull(row.subtaskCompleted) ?? 0,
    createdByName: textOrNull(row.createdByName),
    description: textOrNull(row.description),
    subtasks: objects(row.subtasks)
      .map(normalizeSubtask)
      .filter((item): item is ProjectTaskReportSubtask => item != null),
    timeEntries: objects(row.timeEntries)
      .map(normalizeTimeEntry)
      .filter((item): item is ProjectTaskReportTimeEntry => item != null),
  };
}

function normalizeEmployeeSummary(
  row: ObjectValue,
): ProjectTaskReportEmployeeSummary | null {
  const employeeId = positiveId(row.employeeId);
  if (!employeeId) return null;
  return {
    employeeId,
    employeeNumber: textOrNull(row.employeeNumber),
    employeeName: textOrNull(row.employeeName) ?? 'Employee',
    taskCount: numberOrNull(row.taskCount) ?? 0,
    completedCount: numberOrNull(row.completedCount) ?? 0,
    spentMinutesInRange: numberOrNull(row.spentMinutesInRange) ?? 0,
  };
}

/** Employees the signed-in user may report on in this project (API-scoped). */
export async function getProjectReportEmployees(
  projectId: number,
): Promise<ProjectReportEmployeeOptions> {
  const response = await apiClient.get<
    Envelope<{ canSelectAllEmployees?: unknown; items?: unknown }>
  >(API_ENDPOINTS.PROJECTS.REPORT_EMPLOYEES(projectId));
  const data = response.data.data ?? {};
  return {
    canSelectAllEmployees: data.canSelectAllEmployees === true,
    items: objects(data.items)
      .map(normalizeEmployee)
      .filter((item): item is ProjectReportEmployee => item != null),
  };
}

/** View Report: the same query Download sends, returned as JSON. */
export async function getProjectTaskReport(
  query: ProjectTaskReportQuery,
  signal?: AbortSignal,
): Promise<ProjectTaskReport> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.PROJECTS.REPORT_TASKS(query.projectId),
    { params: { ...projectTaskReportParams(query), format: 'JSON' }, signal },
  );
  const data = response.data.data ?? {};
  const filters = (data.filters ?? {}) as ObjectValue;
  const summary = (data.summary ?? {}) as ObjectValue;
  const project = (data.project ?? {}) as ObjectValue;
  const rows = objects(data.rows)
    .map(normalizeRow)
    .filter((item): item is ProjectTaskReportRow => item != null);
  return {
    generatedAt: textOrNull((data.report as ObjectValue | undefined)?.generatedAt),
    project: {
      projectId: positiveId(project.projectId) ?? query.projectId,
      projectName: textOrNull(project.projectName) ?? 'Project',
    },
    filters: {
      period: normalizePeriod(filters.period),
      fromDate: calendarDateFromApi(filters.fromDate) ?? '',
      toDate: calendarDateFromApi(filters.toDate) ?? '',
      employeeId: positiveId(filters.employeeId),
      employeeName: textOrNull(filters.employeeName),
    },
    summary: {
      rowCount: numberOrNull(summary.rowCount) ?? rows.length,
      taskCount: numberOrNull(summary.taskCount) ?? 0,
      projectCount: numberOrNull(summary.projectCount) ?? 0,
      employeeCount: numberOrNull(summary.employeeCount) ?? 0,
      completedCount: numberOrNull(summary.completedCount) ?? 0,
      spentMinutesInRange: numberOrNull(summary.spentMinutesInRange) ?? 0,
    },
    employees: objects(data.employees)
      .map(normalizeEmployeeSummary)
      .filter((item): item is ProjectTaskReportEmployeeSummary => item != null),
    rows,
  };
}
